/**
 * Capability Drawer
 *
 * Full capability detail, laid out as a document rather than a flat stack of
 * equal-weight sections: an open planner proposal (if any) leads, then a
 * Blocked callout when `blockedReason` is set, then paper `ListCard`s for
 * Details (markdown body via `EntityLinkMarkdown`, edited with the WYSIWYG
 * `MarkdownEditor`), Rules (`CapabilityRulesSection` — the per-rule successor
 * to `bodyMd`'s old `## Rules` bullets), and Testing (entry URL / setup /
 * blocked reason as `MetadataItem` rows, collapsed when empty), then Linked flows (search +
 * clickable, new-tab flow links) and Personas, with History collapsed at the
 * bottom since the header's "Last edited by…" line already surfaces the one
 * fact from it worth reading at a glance. Follows `IssueDrawerV2`'s shell:
 * sticky header, scrollable content, footer-free (no destructive actions
 * live here — archive lives on the row).
 *
 * Human-locked fields (`capability.lockedFields`) show a `Lock` icon next to
 * their edit control so it's clear the planner won't overwrite them.
 *
 * @module features/product-map/components/CapabilityDrawer
 */

import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from '@tanstack/react-router';
import { Lock, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAssistant } from '../../assistant/AssistantContext';
import { useExplorationMap } from '../../exploration/map/useExplorationMap';
import {
  Badge,
  Button,
  CollapsibleSection,
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerCloseButton,
  EditableBadge,
  Flex,
  InlineEditableText,
  ListCard,
  LoadingOrbit,
  MarkdownEditor,
  MetadataItem,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@chatsdet/ui';
import { EntityLinkMarkdown } from '../../../ui/components/EntityLinkMarkdown';
import type { ProductMapImportance, ProductMapStatus } from '@chatsdet/types';
import { IMPORTANCE_OPTIONS, IMPORTANCE_VARIANT } from '../importance';
import { STAGE_OPTIONS, STAGE_VARIANT } from '../stage';
import { openQuestionsPrompt, parseOpenQuestionBullets } from '../openQuestions';
import { useCapabilityDetail, useUpdateCapability } from '../useProductMap';
import { COVERAGE_STATUS_BADGE_VARIANT, COVERAGE_STATUS_LABEL, revisionActorName } from '../productMapLogic';
import { CapabilityProposals } from './CapabilityProposals';
import { LinkedFlowsSection } from './LinkedFlowsSection';
import { CapabilityRulesSection } from './CapabilityRulesSection';
import { CapabilityPersonasSection } from './CapabilityPersonasSection';
import { CapabilityHistorySection } from './CapabilityHistorySection';
import type { ProductCapability } from '../types';

function coverageBadgeLabel(capability: ProductCapability): string {
  const label = COVERAGE_STATUS_LABEL[capability.coverageStatus];
  if (!capability.activeLinkCount) return label;
  const flows = capability.activeLinkCount === 1 ? 'flow' : 'flows';
  return `${label} · ${capability.activeLinkCount} ${flows}`;
}

/** A `Lock` icon + tooltip for a field name present in `lockedFields`. Renders nothing otherwise. */
function FieldLock({ field, lockedFields }: { field: string; lockedFields: string[] }) {
  if (!lockedFields.includes(field)) return null;
  return (
    <TooltipTrigger delay={150}>
      <span className="inline-flex shrink-0 text-muted-foreground" aria-label="Locked by a human edit">
        <Lock size={13} />
      </span>
      <Tooltip variant="light">Locked by a human edit — the planner won't overwrite it</Tooltip>
    </TooltipTrigger>
  );
}

/**
 * A Testing-card `MetadataItem` row backed by a nullable markdown field:
 * `EntityLinkMarkdown` in read mode, `MarkdownEditor` in edit mode, and a
 * dashed-border placeholder button when empty. Owns its own edit/draft
 * state, reset from `value` whenever not editing (same lifecycle as the
 * Details card's `bodyMd` editor).
 */
function MarkdownMetadataRow({
  label,
  field,
  value,
  placeholder,
  lockedFields,
  onSave,
}: {
  label: string;
  field: string;
  value: string | null;
  placeholder: string;
  lockedFields: string[];
  onSave: (value: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  return (
    <MetadataItem
      label={label}
      action={
        !editing && (
          <Flex align="center" gap="xs">
            <FieldLock field={field} lockedFields={lockedFields} />
            <Button variant="ghost" size="sm" iconLeft={<PencilSimple size={12} />} onPress={() => setEditing(true)}>
              Edit
            </Button>
          </Flex>
        )
      }
    >
      {editing ? (
        <div className="space-y-2 py-1">
          <MarkdownEditor value={draft} onChange={setDraft} placeholder={placeholder} />
          <Flex gap="sm">
            <Button
              size="sm"
              onPress={() => {
                onSave(draft.trim() || null);
                setEditing(false);
              }}
            >
              Save
            </Button>
            <Button variant="ghost" size="sm" onPress={() => setEditing(false)}>
              Cancel
            </Button>
          </Flex>
        </div>
      ) : value ? (
        <EntityLinkMarkdown compact className="text-sm leading-relaxed">
          {value}
        </EntityLinkMarkdown>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={label}
          className="w-full rounded-none border-2 border-dashed border-border py-2 text-left text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
        >
          {placeholder}
        </button>
      )}
    </MetadataItem>
  );
}

/**
 * Read-only: `openQuestionsMd` split into one row per bullet, each with an
 * "Answer" button that sends the assistant to work this question as a lead
 * on a fresh thread (Decision 2026-09-17) — a human never edits this list
 * directly, since reconcile owns and rewrites it every cycle.
 */
function OpenQuestionsRow({ capability }: { capability: ProductCapability }) {
  const { askAssistant } = useAssistant();
  const questions = parseOpenQuestionBullets(capability.openQuestionsMd);

  return (
    <MetadataItem label="Open questions">
      {questions.length === 0 ? (
        <Text size="sm" className="text-muted-foreground italic">
          No open questions.
        </Text>
      ) : (
        <div className="space-y-2 py-1">
          {questions.map((question, index) => (
            <Flex key={index} align="start" justify="between" gap="sm">
              <Text size="sm" className="leading-relaxed">
                {question}
              </Text>
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0"
                onPress={() =>
                  askAssistant(
                    openQuestionsPrompt({ code: capability.code, title: capability.title, question }),
                    { propertyId: capability.propertyId }
                  )
                }
              >
                Answer
              </Button>
            </Flex>
          ))}
        </div>
      )}
    </MetadataItem>
  );
}

/**
 * Read-only: the latest exploration outcome for this capability — exploration's
 * own reach layer, never the "Coverage blocked" callout above (human-set).
 * Feature-gated (`exploration`) since it reads `useExplorationMap`, itself
 * behind that flag's routes/nav.
 */
function CapabilityExplorationSection({ capability }: { capability: ProductCapability }) {
  const { data: overlay } = useExplorationMap(capability.propertyId);
  const outcome = overlay?.capabilityOutcomes.find((o) => o.capabilityId === capability.id);
  if (!outcome) return null;

  return (
    <CollapsibleSection label="Exploration" hideBorder>
      <ListCard>
        <div className="px-4 py-3 text-sm">
          <Text size="sm">
            Last explored {formatDistanceToNow(new Date(outcome.exploredAt))} ago ·{' '}
            {outcome.outcome === 'exercised' ? 'Exercised' : 'Unreached'}
            {outcome.reason ? ` — ${outcome.reason}` : ''}
          </Text>
          {outcome.cycleId && (
            <Link
              to="/product-map/cycles/$cycleId"
              params={{ cycleId: outcome.cycleId }}
              className="text-xs text-primary hover:underline"
            >
              View exploration cycle
            </Link>
          )}
        </div>
      </ListCard>
    </CollapsibleSection>
  );
}

type Props = {
  capabilityId: string;
};

export function CapabilityDrawer({ capabilityId }: Props) {
  const { data: capability, isLoading } = useCapabilityDetail(capabilityId);
  const updateCapability = useUpdateCapability();
  const { user } = useAuth();
  const explorationEnabled = Boolean(user?.features?.exploration);

  const [editingBody, setEditingBody] = useState(false);
  const [bodyDraft, setBodyDraft] = useState('');

  useEffect(() => {
    if (!editingBody) setBodyDraft(capability?.bodyMd ?? '');
  }, [capability?.bodyMd, editingBody]);

  if (isLoading || !capability) {
    return (
      <Drawer hideCloseButton>
        <div className="flex h-full items-center justify-center">
          <LoadingOrbit size="lg" />
        </div>
      </Drawer>
    );
  }

  const appliedRevisions = capability.revisions.filter((r) => r.changeKind !== 'proposal_rejected');
  const latestRevision = appliedRevisions[appliedRevisions.length - 1];

  // Counts what the Testing card actually shows: a set blocked reason renders
  // as the callout above the card, never as a row inside it.
  const testingFilledCount = [capability.entryUrlPattern, capability.setupMd, capability.openQuestionsMd].filter((v) =>
    Boolean(v)
  ).length;

  return (
    <Drawer hideCloseButton>
      <DrawerHeader className="space-y-3">
        <Flex align="start" justify="between" gap="md">
          <Flex align="center" gap="sm" className="pt-0.5">
            <EditableBadge<ProductMapImportance>
              value={capability.importance}
              options={IMPORTANCE_OPTIONS}
              onChange={(importance) => updateCapability.mutate({ id: capability.id, importance })}
              variant={IMPORTANCE_VARIANT[capability.importance]}
              size="sm"
              aria-label={`Importance for ${capability.title}`}
            />
            <EditableBadge<ProductMapStatus>
              value={capability.status}
              options={STAGE_OPTIONS}
              onChange={(status) => updateCapability.mutate({ id: capability.id, status })}
              variant={STAGE_VARIANT[capability.status]}
              size="sm"
              aria-label={`Stage for ${capability.title}`}
            />
            {capability.code && (
              <Text size="xs" className="font-mono text-muted-foreground/70">
                {capability.code}
              </Text>
            )}
            <Badge size="sm" variant={COVERAGE_STATUS_BADGE_VARIANT[capability.coverageStatus]}>
              {coverageBadgeLabel(capability)}
            </Badge>
          </Flex>
          <DrawerCloseButton className="-mt-1.5 -mr-2" />
        </Flex>

        <div className="space-y-2">
          <InlineEditableText
            value={capability.title}
            onSave={(title) => updateCapability.mutate({ id: capability.id, title })}
            ariaLabel="Capability title"
            placeholder="Untitled capability"
            className="text-xl font-semibold leading-tight"
            inputClassName="text-xl font-semibold leading-tight"
          />

          {capability.previousCodes.length > 0 && (
            <Text size="xs" className="font-mono text-muted-foreground/60">
              formerly {capability.previousCodes.join(', ')}
            </Text>
          )}

          <InlineEditableText
            value={capability.description ?? ''}
            onSave={(description) => updateCapability.mutate({ id: capability.id, description })}
            ariaLabel="Capability description"
            placeholder="Add a one-line description…"
            // Optional field: clearing it back to empty must persist.
            allowEmpty
            className="text-sm text-muted-foreground"
          />

          {latestRevision && (
            <Text size="xs" className="text-muted-foreground/70">
              Last edited by {revisionActorName(latestRevision)} ·{' '}
              {formatDistanceToNow(new Date(latestRevision.createdAt))} ago
            </Text>
          )}
        </div>
      </DrawerHeader>

      <DrawerContent className="space-y-4">
        <CapabilityProposals capabilityId={capability.id} proposals={capability.openProposals} />

        {capability.blockedReason && (
          <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <Badge variant="destructive">Coverage blocked</Badge>
            <Text size="xs" className="text-muted-foreground">
              Set by a person: regression coverage cannot be added here.
            </Text>
            <InlineEditableText
              value={capability.blockedReason}
              onSave={(blockedReason) => updateCapability.mutate({ id: capability.id, blockedReason: blockedReason || null })}
              ariaLabel="Coverage blocked reason"
              placeholder="Why regression coverage cannot be added, and what would unblock it…"
              allowEmpty
              className="text-sm"
            />
          </div>
        )}

        {capability.coverageStatus === 'blocked' && !capability.blockedReason && (
          <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <Badge variant="destructive">Blocked by an open issue</Badge>
            <Text size="xs" className="text-muted-foreground">
              No flow is linked, and an open issue is linked to this capability or one of its rules.
            </Text>
          </div>
        )}

        <ListCard
          title="Details"
          action={
            !editingBody && (
              <Flex align="center" gap="xs">
                <FieldLock field="bodyMd" lockedFields={capability.lockedFields} />
                <Button variant="ghost" size="sm" iconLeft={<PencilSimple size={12} />} onPress={() => setEditingBody(true)}>
                  Edit
                </Button>
              </Flex>
            )
          }
        >
          {editingBody ? (
            <div className="space-y-2 px-4 py-3">
              <MarkdownEditor
                value={bodyDraft}
                onChange={setBodyDraft}
                placeholder="## Overview / ## Steps / ## Outcome — describe the product, not a run"
              />
              <Flex gap="sm">
                <Button
                  size="sm"
                  onPress={() => {
                    updateCapability.mutate({ id: capability.id, bodyMd: bodyDraft });
                    setEditingBody(false);
                  }}
                  isDisabled={updateCapability.isPending}
                >
                  Save
                </Button>
                <Button variant="ghost" size="sm" onPress={() => setEditingBody(false)}>
                  Cancel
                </Button>
              </Flex>
            </div>
          ) : capability.bodyMd ? (
            <EntityLinkMarkdown compact className="px-4 py-3 text-sm leading-relaxed">
              {capability.bodyMd}
            </EntityLinkMarkdown>
          ) : (
            <button
              type="button"
              onClick={() => setEditingBody(true)}
              className="w-full rounded-none border-2 border-dashed border-border px-4 py-3 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              Add details
            </button>
          )}
        </ListCard>

        <CapabilityRulesSection capabilityId={capability.id} rules={capability.rules} />

        <CollapsibleSection label="Testing" count={testingFilledCount} defaultExpanded={testingFilledCount > 0} hideBorder>
          <ListCard>
            <div className="divide-y divide-border px-4">
              <MetadataItem
                label="Entry URL"
                mono
                action={<FieldLock field="entryUrlPattern" lockedFields={capability.lockedFields} />}
              >
                <InlineEditableText
                  value={capability.entryUrlPattern ?? ''}
                  onSave={(entryUrlPattern) => updateCapability.mutate({ id: capability.id, entryUrlPattern: entryUrlPattern || null })}
                  ariaLabel="Entry URL pattern"
                  placeholder="/projects/:id/settings"
                  allowEmpty
                  className="font-mono"
                />
              </MetadataItem>
              <MarkdownMetadataRow
                label="Setup"
                field="setupMd"
                value={capability.setupMd}
                placeholder="How to reach a testable state…"
                lockedFields={capability.lockedFields}
                onSave={(setupMd) => updateCapability.mutate({ id: capability.id, setupMd })}
              />
              {!capability.blockedReason && (
                <MetadataItem
                  label="Coverage blocked reason"
                  action={<FieldLock field="blockedReason" lockedFields={capability.lockedFields} />}
                >
                  <InlineEditableText
                    value={capability.blockedReason ?? ''}
                    onSave={(blockedReason) => updateCapability.mutate({ id: capability.id, blockedReason: blockedReason || null })}
                    ariaLabel="Coverage blocked reason"
                    placeholder="Set by a person — why regression coverage cannot be added, and what would unblock it…"
                    allowEmpty
                  />
                </MetadataItem>
              )}
              <OpenQuestionsRow capability={capability} />
            </div>
          </ListCard>
        </CollapsibleSection>

        {capability.openIssues.length > 0 && (
          <ListCard title="Open issues" titleAccessory={`${capability.openIssues.length} open`}>
            {capability.openIssues.map((issue) => (
              <div key={`${issue.issueId}:${issue.ruleId ?? ''}`} className="px-4 py-2.5">
                <Link
                  to="/issues"
                  search={{ issueId: issue.issueId }}
                  className="text-sm text-foreground hover:text-primary hover:underline"
                >
                  {issue.title}
                </Link>
                {issue.ruleId && (
                  <Text size="xs" className="text-muted-foreground">
                    Linked to a rule
                  </Text>
                )}
              </div>
            ))}
          </ListCard>
        )}

        <LinkedFlowsSection capabilityId={capability.id} activeWorkflows={capability.activeWorkflows} />

        <CapabilityPersonasSection capabilityId={capability.id} propertyId={capability.propertyId} />

        {explorationEnabled && <CapabilityExplorationSection capability={capability} />}

        <CapabilityHistorySection appliedRevisions={appliedRevisions} />
      </DrawerContent>
    </Drawer>
  );
}
